import * as XLSX from "xlsx";
import html2canvas from "html2canvas";

// Secciones del informe (usadas por el diálogo "descarga por puntos" y por los exports)
export const REPORT_SECTIONS = [
  { key: "resumen", label: "Resumen ejecutivo" },
  { key: "cobranzas", label: "1 · Cobranzas del Periodo" },
  { key: "cuentas", label: "2 · Cuentas por Recibir" },
  { key: "revision", label: "2.1 · Clientes que necesitan revisión" },
  { key: "sin_linea", label: "3 · Clientes sin línea de crédito" },
  { key: "judicial", label: "4 · Casos judiciales" },
  { key: "emitidos", label: "5 · Pagarés Emitidos" },
  { key: "pendientes", label: "6 · Pagarés Pendientes" },
  { key: "visitas", label: "7 · Visitas del Coordinador" },
  { key: "garantias", label: "8 · Garantías en Curso" },
];

// ---- number / date helpers (formato del informe: 1.189.858,02) ----
const esNum = (n) =>
  Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: "always" });

const pctTxt = (p) => (p === null || p === undefined ? "N/A" : `${p}%`);

const esDate = (iso) => {
  if (!iso) return "";
  const p = String(iso).split("-");
  if (p.length === 3 && p[0].length === 4) return `${p[2].slice(0, 2)}/${p[1]}/${p[0]}`;
  return String(iso);
};

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function periodLabel(items) {
  const ds = (items || []).map((i) => i.fecha_acreditacion).filter(Boolean).sort();
  const s = ds.length ? ds[Math.floor(ds.length / 2)] : new Date().toISOString().slice(0, 10);
  const [y, m] = s.split("-");
  return { mes: MONTHS[parseInt(m, 10) - 1] || "", anio: y || new Date().getFullYear() };
}

function today() {
  const d = new Date();
  return `${d.getDate()} de ${MONTHS[d.getMonth()]} de ${d.getFullYear()}`;
}

function loadLogo() {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.width;
        c.height = img.height;
        c.getContext("2d").drawImage(img, 0, 0);
        resolve({ data: c.toDataURL("image/png"), w: img.width, h: img.height });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = "/logo-tecnomyl.png";
  });
}


// ============================ Excel ============================
export function exportExcel(report, opts = {}) {
  const wb = XLSX.utils.book_new();
  const inc = (k) => !opts.sections || opts.sections.includes(k);

  if (inc("cobranzas")) {
  const cob = report.cobranzas.items.map((r) => ({
    Cliente: r.cliente, Vendedor: r.vendedor,
    "Monto Total (USD)": r.monto_usd, "Cobro Acumulado Cliente (USD)": r.cobro_acumulado_cliente,
  }));
  cob.push({});
  cob.push({ Cliente: "TOTAL COBRADO DEL MES", "Monto Total (USD)": report.cobranzas.total_cobrado_mes });
  cob.push({ Cliente: "TOTAL ACUMULADO DEL AÑO", "Monto Total (USD)": report.cobranzas.total_acumulado_anio });
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(cob), "1-Cobranzas");
  }

  if (inc("cuentas")) {
  const cta = report.cuentas.items.map((r) => ({
    Cliente: r.cliente, "Valor Vencido": r.valor_vencido, "Valor A Vencer": r.valor_a_vencer,
    "Total Cartera": r.total_cartera, "Línea Total": r.linea_total, "% Línea (Fact.)": pctTxt(r.pct_linea_facturado),
    "Fact.+Pend.": r.valor_linea_usd,
    "Línea Disp. vs Fact.": r.saldo_disponible == null ? "N/A" : r.saldo_disponible,
    "Línea Disp. vs Fact.+Pend.": r.disponible_neto == null ? "N/A" : r.disponible_neto,
    "% Fact.+Pend.": pctTxt(r.pct_utilizacion),
  }));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(cta), "2-CuentasPorRecibir");
  }

  if (inc("revision")) {
  const rev = report.cuentas.items
    .filter((r) => r.disponible_neto != null && r.disponible_neto < 0)
    .map((r) => ({
      Cliente: r.cliente, "Línea Total": r.linea_total, "Fact.+Pend.": r.valor_linea_usd,
      "Total Cartera": r.total_cartera, "Línea Disp. vs Fact.+Pend.": r.disponible_neto,
    }));
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(rev.length ? rev : [{ Cliente: "Sin clientes con Disponible Neto negativo" }]),
    "2.1-ClientesRevision"
  );
  }

  if (inc("emitidos"))
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(report.pagaresEmitidos.items.map((r) => ({
    Cliente: r.cliente, "Cantidad de Pagarés": r.cantidad, "Valor Total (USD)": r.valor_total_usd,
  }))), "3-PagaresEmitidos");

  if (inc("pendientes"))
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(report.pagaresPendientes.items.map((r) => ({
    "Cant. Pagarés": r.cantidad, Cliente: r.cliente, "Valor Total (USD)": r.valor_total_usd,
    Emisión: esDate(r.fecha_emision), "Días desde Emisión": r.dias_transcurridos,
    "Observación / Alerta": r.alerta ? "¡Atención! +60 días desde emisión sin firma" : (r.dias_transcurridos > 0 ? "En proceso" : "Reciente"),
  }))), "4-PagaresPendientes");

  if (inc("visitas"))
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(report.visitas.map((r) => ({
    Fecha: r.fecha, Vendedor: r.vendedor || "-", Cliente: r.cliente, Motivo: r.motivo, Acciones: r.acciones, Conclusiones: r.conclusiones,
  }))), "5-Visitas");

  if (inc("garantias"))
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(report.garantias.map((r) => ({
    Tipo: r.tipo, Cliente: r.cliente, Descripción: r.descripcion, "Valor Estimado (USD)": r.valor_estimado_usd, Estado: r.estado,
  }))), "6-Garantias");

  if (!wb.SheetNames.length)
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([{ Aviso: "No se seleccionó ninguna sección" }]), "Informe");

  XLSX.writeFile(wb, `Informe_Gestion_Cartera_Tecnomyl_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

// ============================ Foto HD (PNG alta resolución + marca de agua) ============================
export async function exportImageHD(elementId, opts = {}) {
  const el = document.getElementById(elementId);
  if (!el) throw new Error("No se encontró el contenido del informe para capturar.");

  const canvas = await html2canvas(el, {
    scale: opts.scale || 2.5,
    backgroundColor: "#f4f7f5",
    useCORS: true,
    logging: false,
    scrollX: 0,
    scrollY: -window.scrollY,
    windowWidth: el.scrollWidth,
    windowHeight: el.scrollHeight,
    onclone: (doc) => doc.documentElement.classList.remove("tm-oscuro"),   // la foto sale siempre en tema claro
  });

  // Marca de agua corporativa sutil (logo repetido en diagonal, baja opacidad)
  const wm = await loadWatermarkImage();
  if (wm) {
    const ctx = canvas.getContext("2d");
    const tileW = canvas.width * 0.26;
    const tileH = (wm.naturalHeight / wm.naturalWidth) * tileW;
    ctx.save();
    ctx.globalAlpha = 0.05;
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(-Math.PI / 9);
    ctx.translate(-canvas.width / 2, -canvas.height / 2);
    for (let yy = -tileH; yy < canvas.height + tileH; yy += tileH * 2.4) {
      for (let xx = -tileW; xx < canvas.width + tileW; xx += tileW * 1.6) {
        ctx.drawImage(wm, xx, yy, tileW, tileH);
      }
    }
    ctx.restore();
  }

  const url = canvas.toDataURL("image/png", 1.0);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Informe_Gestion_Cartera_Tecnomyl_${new Date().toISOString().slice(0, 10)}.png`;
  a.click();
}

// carga el logo como HTMLImageElement (ya decodificado) para dibujar el watermark
function loadWatermarkImage() {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = "/logo-tecnomyl.png";
  });
}
